/**
 * Chummys arch divider — <chummys-arch>
 *
 * Fills the band with a single cubic bezier. One number, `y`, positions both
 * the curve's endpoints and (inversely) its control points, so as `y` grows the
 * arch rolls through flat and comes out inverted:
 *
 *   y = 0    endpoints at the edge, controls pushed deep  -> full bulge
 *   y = 0.5  endpoints and controls level                 -> flat
 *   y = 1    endpoints deep, controls at the edge         -> bulge reversed
 *
 * Scroll maps 0..1 across the element's travel through the viewport. Drawing
 * only runs while the element is on screen and only when `y` actually moved.
 */
(() => {
  if (customElements.get('chummys-arch')) return;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ChummysArch extends HTMLElement {
    constructor() {
      super();
      this.y = 0;
      this.drawnY = -1;
      this.visible = false;
      this.raf = null;
      this.w = 0;
      this.h = 0;
      this.onScroll = this.onScroll.bind(this);
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      if (!this.canvas || !this.canvas.getContext) return;
      this.ctx = this.canvas.getContext('2d');

      this.fill = this.dataset.fill || '#faf7f0';
      this.curve = parseFloat(this.dataset.curve) || 0.7;
      this.flip = parseFloat(this.dataset.flip);
      if (!Number.isFinite(this.flip)) this.flip = 0.7;
      this.up = this.dataset.direction !== 'down';
      this.isStatic = this.hasAttribute('data-static') || mqReduced.matches;

      this.resize();
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(() => { this.resize(); this.drawnY = -1; this.request(); });
        this.ro.observe(this.canvas);
      }

      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          this.visible = e.isIntersecting;
          if (this.visible) this.request();
        });
      }, { threshold: 0 });
      this.io.observe(this);

      if (!this.isStatic) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
      }

      this.update();
      this.request();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = this.canvas.offsetWidth;
      const h = this.canvas.offsetHeight;
      if (!w || !h) return;
      this.w = w;
      this.h = h;
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    onScroll() {
      this.update();
      this.request();
    }

    update() {
      if (this.isStatic) { this.y = 0; return; }
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // 0 when the band's top first touches the bottom of the viewport,
      // 1 once its bottom has risen past ~a third of the way up the screen.
      const travel = vh + rect.height;
      const progress = clamp((vh - rect.top) / travel, 0, 1);
      this.y = progress * this.flip;
    }

    request() {
      if (this.raf !== null || !this.visible) return;
      this.raf = requestAnimationFrame(this.tick);
    }

    tick() {
      this.raf = null;
      if (!this.visible || !this.w || !this.h) return;
      if (Math.abs(this.y - this.drawnY) < 0.0015) return;
      this.draw();
      this.drawnY = this.y;
    }

    draw() {
      const { ctx, w, h, curve } = this;
      const y = this.y;
      const edge = y * h;                 // where the curve meets the left/right walls
      const control = (1 - y) * h * curve; // how far the middle is pushed the other way

      ctx.clearRect(0, 0, w, h);
      ctx.beginPath();

      if (this.up) {
        // filled mass sits along the bottom edge, curve on top
        ctx.moveTo(0, h);
        ctx.lineTo(0, h - edge);
        ctx.bezierCurveTo(0.25 * w, h - control, 0.75 * w, h - control, w, h - edge);
        ctx.lineTo(w, h);
      } else {
        // filled mass hangs from the top edge, curve underneath
        ctx.moveTo(0, 0);
        ctx.lineTo(0, edge);
        ctx.bezierCurveTo(0.25 * w, control, 0.75 * w, control, w, edge);
        ctx.lineTo(w, 0);
      }

      ctx.closePath();
      ctx.fillStyle = this.fill;
      ctx.fill();
    }
  }

  customElements.define('chummys-arch', ChummysArch);
})();
